import React, { useEffect, useState } from "react";
import { api } from "../services/api";
import ProdutoForm, { Produto } from "./ProdutoForm";
import CategoriaForm, { CategoriaProduto } from "./CategoriaForm";
import Swal from "sweetalert2";

const ProdutoPage: React.FC = () => {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [categorias, setCategorias] = useState<CategoriaProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showCategoriaForm, setShowCategoriaForm] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState<Produto | null>(null);
  const [categoriaEditando, setCategoriaEditando] = useState<CategoriaProduto | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [loadingCategorias, setLoadingCategorias] = useState(false);

  // 🔹 Buscar todos os produtos
  const fetchProdutos = async () => {
    try {
      setLoading(true);
      const response = await api.get<Produto[]>("/produto");
      setProdutos(response.data);
      setError(null);
    } catch (err: any) {
      console.error("Erro ao buscar produtos:", err);
      setError("Não foi possível carregar os produtos.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Buscar categorias
  const fetchCategorias = async () => {
    try {
      setLoadingCategorias(true);
      const response = await api.get<CategoriaProduto[]>("/CategoriaProduto");
      setCategorias(response.data);
    } catch (err: any) {
      console.error("Erro ao buscar categorias:", err);
      Swal.fire("Erro", "Não foi possível carregar as categorias.", "error");
    } finally {
      setLoadingCategorias(false);
    }
  };

  useEffect(() => {
    fetchProdutos();
  }, []);

  // 🔹 Excluir produto com confirmação
  const handleDelete = async (id: number) => {
    const resultado = await Swal.fire({
      title: "Excluir produto?",
      text: "Essa ação não poderá ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    });

    if (!resultado.isConfirmed) return;

    setDeletingId(id);
    try {
      await api.delete(`/produto/${id}`);
      setProdutos((prev) => prev.filter((p) => p.id !== id));
      Swal.fire("Excluído!", "O produto foi removido com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao excluir produto:", error);
      Swal.fire("Erro", "Não foi possível excluir o produto.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  // 🔹 Excluir categoria
  const handleDeleteCategoria = async (id: number) => {
    const resultado = await Swal.fire({
      title: "Excluir categoria?",
      text: "Essa ação não poderá ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    });

    if (!resultado.isConfirmed) return;

    try {
      await api.delete(`/CategoriaProduto/${id}`);
      setCategorias((prev) => prev.filter((c) => c.id !== id));
      Swal.fire("Excluída!", "A categoria foi removida com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao excluir categoria:", error);
      Swal.fire("Erro", "Não foi possível excluir a categoria.", "error");
    }
  };

  // 🔹 Editar produto
  const handleEdit = (produto: Produto) => {
    setProdutoEditando(produto);
    setShowForm(true);
    setShowCategoriaForm(false);
  };

  // 🔹 Editar categoria
  const handleEditCategoria = (categoria: CategoriaProduto) => {
    setCategoriaEditando(categoria);
    setShowCategoriaForm(true);
    setShowForm(false);
  };

  // 🔹 Após salvar produto
  const handleSave = async () => {
    setShowForm(false);
    setProdutoEditando(null);
    await fetchProdutos();
  };

  // 🔹 Após salvar categoria
  const handleCategoriaSave = async () => {
    setShowCategoriaForm(false);
    setCategoriaEditando(null);
    Swal.fire("Sucesso!", "Categoria salva com sucesso!", "success");
    await fetchCategorias();
  };

return (
  <div className="container-fluid">
    {/* Cabeçalho da página */}
    <div className="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 className="mb-1">
          📦 {showCategoriaForm ? "Categorias" : "Produtos"}
        </h2>
        <p className="text-muted mb-0">
          {showCategoriaForm
            ? "Gerencie as categorias de produtos"
            : "Gerencie os produtos cadastrados"}
        </p>
      </div>

      <div className="d-flex gap-2">
        {/* Botão Categorias */}
        <button
          className="btn btn-outline-secondary"
          onClick={() => {
            const next = !showCategoriaForm;
            setShowCategoriaForm(next);
            setShowForm(false);
            if (next) fetchCategorias();
          }}
        >
          {showCategoriaForm ? "Voltar aos Produtos" : "Categorias"}
        </button>

        {/* Botão Novo Produto */}
        {!showCategoriaForm && (
          <button
            className="btn btn-primary"
            onClick={() => {
              setProdutoEditando(null);
              setShowForm(!showForm);
            }}
          >
            {showForm ? "Fechar Formulário" : "+ Novo Produto"}
          </button>
        )}
      </div>
    </div>

    {/* ===== CATEGORIAS ===== */}
    {showCategoriaForm ? (
      <>
        <CategoriaForm
          categoria={categoriaEditando ?? undefined}
          onSave={handleCategoriaSave}
          onCancel={() => {
            setShowCategoriaForm(false);
            setCategoriaEditando(null);
          }}
        />

        <div className="table-responsive mt-4">
          {loadingCategorias ? (
            <p>Carregando categorias...</p>
          ) : categorias.length > 0 ? (
            <table className="table table-striped table-hover align-middle">
              <thead className="table-warning">
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {categorias.map((cat) => (
                  <tr key={cat.id}>
                    <td>{cat.id}</td>
                    <td>{cat.nome}</td>
                    <td>{cat.descricao}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEditCategoria(cat)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteCategoria(cat.id!)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="alert alert-warning mt-3">
              Nenhuma categoria cadastrada.
            </div>
          )}
        </div>
      </>
    ) : (
      <>
        {/* ===== FORMULÁRIO ===== */}
        {showForm && (
          <ProdutoForm
            produto={produtoEditando ?? undefined}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setProdutoEditando(null);
            }}
          />
        )}

        {/* Status */}
        {loading && <p>Carregando produtos...</p>}
        {error && <div className="alert alert-danger">{error}</div>}

        {/* ===== TABELA ===== */}
        {!loading && !error && produtos.length > 0 && (
          <div className="table-responsive mt-4">
            <table className="table table-striped table-hover align-middle">
              <thead className="table-warning">
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Preço Venda</th>
                  <th>Estoque</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {produtos.map((produto) => (
                  <tr key={produto.id}>
                    <td>{produto.id}</td>
                    <td>{produto.nome}</td>
                    <td>R$ {produto.precoVenda.toFixed(2)}</td>
                    <td>{produto.quantidadeEstoque}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleEdit(produto)}
                        disabled={deletingId === produto.id}
                      >
                        Editar
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(produto.id!)}
                        disabled={deletingId === produto.id}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && produtos.length === 0 && (
          <div className="alert alert-warning mt-3">
            Nenhum produto cadastrado.
          </div>
        )}
      </>
    )}
  </div>
);
}
export default ProdutoPage;
